type locales = "deDE" | "enUS" | "itIT"
type markets = "DE" | "IT" | "AU" | "AT"
type languages = "de" | "it" | "en" | "fr"

/** Serves as initial language and should be used as fallback by each micro frontend. */
export const fallbackLanguage: languages = "de"
export const fallbackMarket: markets = "DE"
export const localStorageLanguageKey = "language"
export const localStorageMarketKey = "market"

/** Returns the currently selected language. */
export function currentLanguage(): string {
    const storedLanguage = localStorage.getItem(localStorageLanguageKey)
    return storedLanguage ?? fallbackLanguage
}

/**
 * Switches to the given language.
 * The returned Promise resolves if the language switch was successfully performed and rejects otherwise.
 */
export function switchLanguage(newLanguage: languages, withReload: boolean = true): Promise<void> {
    localStorage.setItem(localStorageLanguageKey, newLanguage)
    if (withReload) window.location.reload()
    return Promise.resolve()
}

function languageByMarket(): languages {
    switch (currentMarket()) {
        case "DE":
            return "de"
        case "IT":
            return "it"
        case "AU":
            return "en"
        default:
            return "de"
    }
}

export function switchLanguageByMarket(withReload: boolean = true): Promise<void> {
    return switchLanguage(languageByMarket(), withReload)
}

/**
 * Returns the formatter to format simple dates.
 * @param lng the language to format to. If not given, the [currentLanguage] is used.
 *
 */
export function dateFormatter(lng?: string): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(lng ?? currentLanguage(), {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    })
}

/**
 * Returns the locale from the current language.
 */
export function currentLocale(): locales {
    const language = currentLanguage()
    let localization = "deDE"
    if (language === "en") {
        localization = "enUS"
    } else if (language === "it") {
        localization = "itIT"
    }
    return <"deDE" | "enUS" | "itIT">localization
}

/** Returns the users / currently selected market. */
export function currentMarket(): string {
    const storedMarket = localStorage.getItem(localStorageMarketKey)
    return storedMarket ?? fallbackMarket
}

/**
 * Switches to the given market
 * Only reloads the site if withReload is true
 * The returned Promise resolves if the market switch was successfully performed and rejects otherwise.
 */
export function switchMarket(newMarket: string, withReload: boolean = true) {
    localStorage.setItem(localStorageMarketKey, newMarket)
    if (withReload) window.location.reload()
    return Promise.resolve()
}
